import React, { useState } from 'react';
import { FRAMEWORKS } from './data/frameworks';

const CATEGORY_LABELS = {
  defense:    'Defense & Federal',
  commercial: 'Commercial',
  healthcare: 'Healthcare',
  finance:    'Finance',
};

const CATEGORY_ORDER = ['defense', 'commercial', 'healthcare', 'finance'];

// Which frameworks each plan can access
const PLAN_FRAMEWORK_ACCESS = {
  professional: ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171'],
  business:     ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171', 'soc2', 'iso27001', 'hipaa', 'nist_csf', 'pci_dss', 'fedramp'],
  // Legacy aliases for backward compat
  starter:      ['cmmc_l1'],
  pro:          ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171'],
  team:         ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171', 'soc2', 'iso27001', 'hipaa', 'nist_csf'],
  agency:       ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171', 'soc2', 'iso27001', 'hipaa', 'nist_csf', 'pci_dss', 'fedramp'],
};

const PLAN_HIERARCHY = { professional: 1, business: 2, starter: 1, pro: 1, team: 2, agency: 2 };

// Returns the minimum plan that unlocks a given framework
function requiredPlanForFramework(fwId) {
  for (const plan of ['professional', 'business']) {
    if (PLAN_FRAMEWORK_ACCESS[plan].includes(fwId)) return plan;
  }
  return 'business';
}

const PLAN_LABELS = { professional: 'Professional', business: 'Business' };

function CategoryBadge({ category }) {
  const styles = {
    defense:    { bg: 'var(--blue-bg)',    color: 'var(--accent)' },
    commercial: { bg: 'var(--amber-bg)',   color: 'var(--amber)'  },
    healthcare: { bg: 'rgba(6,182,212,.1)', color: '#06B6D4'      },
    finance:    { bg: 'var(--red-bg)',     color: 'var(--red)'    },
  };
  const s = styles[category] || styles.commercial;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
      background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

function FrameworkCard({ fw, isCurrent, canAccess, requiredPlan, onSelect, onUpgrade }) {
  const [hovered, setHovered] = useState(false);
  const isActive  = isCurrent;
  const comingSoon = !fw.available;
  const locked     = fw.available && !canAccess;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:    locked ? 'var(--surface)' : 'var(--bg2)',
        border:        isActive
          ? `2px solid ${fw.color}`
          : hovered && canAccess && fw.available
            ? '1px solid var(--border2)'
            : '1px solid var(--border)',
        borderRadius:  12,
        padding:       isActive ? 19 : 20,
        display:       'flex',
        flexDirection: 'column',
        gap:           12,
        opacity:       comingSoon ? 0.52 : 1,
        transition:    'all 0.18s',
        position:      'relative',
        overflow:      'hidden',
        boxShadow:     isActive
          ? `0 0 0 1px ${fw.color}30, 0 4px 20px ${fw.color}18`
          : hovered && canAccess && fw.available
            ? '0 2px 12px rgba(0,0,0,0.15)'
            : 'none',
      }}
    >
      {/* Active glow strip */}
      {isActive && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: fw.color, borderRadius: '12px 12px 0 0',
        }} />
      )}

      {/* Lock overlay for plan-gated frameworks */}
      {locked && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 12,
          background: 'rgba(10,14,26,0.55)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 8, zIndex: 2,
          backdropFilter: 'blur(1px)',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--amber)',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', textAlign: 'center' }}>
            {PLAN_LABELS[requiredPlan]} Plan Required
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', maxWidth: 180, lineHeight: 1.5 }}>
            Upgrade to access {fw.name}
          </div>
          <button
            onClick={onUpgrade}
            style={{
              fontSize: 11, fontWeight: 600, padding: '6px 16px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--amber)',
              background: 'var(--amber-bg)', color: 'var(--amber)',
              fontFamily: 'var(--font)', transition: 'all 0.15s', marginTop: 2,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--amber)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--amber-bg)'; e.currentTarget.style.color = 'var(--amber)'; }}
          >
            Upgrade to {PLAN_LABELS[requiredPlan]}
          </button>
        </div>
      )}

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 9, flexShrink: 0,
            background: `${fw.color}18`,
            border: `1.5px solid ${fw.color}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{
              fontSize: 11, fontWeight: 800, color: fw.color,
              fontFamily: 'var(--mono)', letterSpacing: '-0.03em',
            }}>
              {fw.shortName.split(' ')[0]}
            </span>
          </div>
          <div>
            <div style={{
              fontSize: 13, fontWeight: 700, lineHeight: 1.2,
              color: comingSoon ? 'var(--text3)' : 'var(--text)',
            }}>
              {fw.name}
            </div>
            <div style={{ marginTop: 4 }}>
              <CategoryBadge category={fw.category} />
            </div>
          </div>
        </div>

        {/* Status badge */}
        {fw.available ? (
          isActive ? (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 8,
              background: `${fw.color}18`, color: fw.color,
              border: `1px solid ${fw.color}60`, whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              Active
            </span>
          ) : null
        ) : (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 8,
            background: 'var(--surface2)', color: 'var(--text3)',
            border: '1px solid var(--border)', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            Coming Soon
          </span>
        )}
      </div>

      {/* Description */}
      <div style={{
        fontSize: 12, color: 'var(--text3)', lineHeight: 1.55, flex: 1,
      }}>
        {fw.description}
      </div>

      {/* Footer row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 7, height: 7, borderRadius: '50%', background: fw.color, flexShrink: 0,
          }} />
          <span style={{
            fontSize: 12, fontWeight: 600, color: fw.color,
            fontFamily: 'var(--mono)',
          }}>
            {fw.controlCount}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>controls</span>
        </div>

        {fw.available && !isActive && canAccess && (
          <button
            onClick={() => onSelect(fw.id)}
            style={{
              fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 6,
              cursor: 'pointer', border: `1px solid ${fw.color}`,
              background: hovered ? fw.color : 'transparent',
              color: hovered ? '#fff' : fw.color,
              fontFamily: 'var(--font)', transition: 'all 0.15s',
            }}
          >
            Select
          </button>
        )}

        {fw.available && isActive && (
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>Currently active</span>
        )}

        {!fw.available && (
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>Notify me</span>
        )}
      </div>
    </div>
  );
}

export default function FrameworkSelector({ currentFramework, onSelect, onUpgrade, userPlan = 'professional', allowedFrameworks }) {
  const [filterCategory, setFilterCategory] = useState('all');

  // If allowedFrameworks is a non-empty array, the member has restricted access
  const hasMemberRestriction = Array.isArray(allowedFrameworks) && allowedFrameworks.length > 0;
  const accessibleIds = hasMemberRestriction
    ? allowedFrameworks
    : (PLAN_FRAMEWORK_ACCESS[userPlan] || PLAN_FRAMEWORK_ACCESS.starter);

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = FRAMEWORKS.filter(fw => {
      if (hasMemberRestriction && !allowedFrameworks.includes(fw.id)) return false;
      return fw.category === cat &&
        (filterCategory === 'all' || fw.category === filterCategory);
    });
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const currentFw = FRAMEWORKS.find(f => f.id === currentFramework) || FRAMEWORKS[1];

  // Determine next upgrade plan name for the CTA
  const currentPlanLevel = PLAN_HIERARCHY[userPlan] || 1;
  const upgradePlanName  = Object.entries(PLAN_HIERARCHY).find(([, v]) => v === currentPlanLevel + 1)?.[0];
  const upgradeLabel     = upgradePlanName ? PLAN_LABELS[upgradePlanName] : null;
  const lockedCount      = FRAMEWORKS.filter(fw => fw.available && !accessibleIds.includes(fw.id)).length;

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 5 }}>
          Compliance Frameworks
        </div>
        <div style={{
          fontSize: 12, color: 'var(--text3)', lineHeight: 1.65, maxWidth: 680,
        }}>
          TheClearAudit supports multiple compliance frameworks so you can assess your organization
          against the standards that matter most to your business. Select a framework below to
          switch your assessment questionnaire, control mapping, and reporting to that standard.
          Your answers are stored per-framework and never mixed between them.
        </div>
      </div>

      {/* Active framework banner */}
      <div style={{
        background: `${currentFw.color}10`,
        border: `1px solid ${currentFw.color}40`,
        borderRadius: 10, padding: '14px 18px', marginBottom: lockedCount > 0 ? 14 : 24,
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: `${currentFw.color}20`,
          border: `1.5px solid ${currentFw.color}60`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{
            fontSize: 11, fontWeight: 800, color: currentFw.color,
            fontFamily: 'var(--mono)',
          }}>
            {currentFw.shortName.split(' ')[0]}
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 2 }}>
            Active Framework
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: currentFw.color }}>
            {currentFw.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
            {currentFw.controlCount} controls · {CATEGORY_LABELS[currentFw.category]}
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'right' }}>
          Your assessment and reports<br />reflect this framework.
        </div>
      </div>

      {/* Plan access notice */}
      {lockedCount > 0 && upgradeLabel && (
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid rgba(245,166,35,0.3)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 12,
        }}>
          <span style={{ color: 'var(--amber)', display: 'flex', alignItems: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </span>
          <span style={{ color: 'var(--text2)', flex: 1 }}>
            <strong style={{ color: 'var(--amber)' }}>{lockedCount} frameworks</strong> are locked on your current plan.
            Upgrade to <strong style={{ color: 'var(--amber)' }}>{upgradeLabel}</strong> to unlock more frameworks.
          </span>
        </div>
      )}

      {/* Category filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', ...CATEGORY_ORDER].map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            style={{
              fontSize: 11, fontWeight: 500, padding: '5px 12px', borderRadius: 6,
              cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.15s',
              border: filterCategory === cat ? '1px solid var(--accent)' : '1px solid var(--border2)',
              background: filterCategory === cat ? 'var(--accent)' : 'var(--surface)',
              color: filterCategory === cat ? '#fff' : 'var(--text3)',
            }}
          >
            {cat === 'all' ? 'All Frameworks' : CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Framework groups */}
      {Object.entries(grouped).map(([cat, frameworks]) => (
        <div key={cat} style={{ marginBottom: 32 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--text3)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: 12, paddingBottom: 8,
            borderBottom: '1px solid var(--border)',
          }}>
            {CATEGORY_LABELS[cat]}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 14,
          }}>
            {frameworks.map(fw => (
              <FrameworkCard
                key={fw.id}
                fw={fw}
                isCurrent={fw.id === currentFramework}
                canAccess={accessibleIds.includes(fw.id)}
                requiredPlan={requiredPlanForFramework(fw.id)}
                onSelect={onSelect}
                onUpgrade={onUpgrade}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Info panel */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '16px 18px', marginTop: 8,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
          How frameworks work in TheClearAudit
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {[
            'Each framework has its own set of controls, questionnaire, and scoring methodology',
            'Switching frameworks changes your Assessment, Gap Analysis, and Reports views',
            'Your answers are stored per-framework — switching never overwrites existing data',
            'Available frameworks are ready to use today; Coming Soon frameworks are in development',
            'CMMC Level 2 and NIST SP 800-171 share the same 110 controls with different control IDs',
          ].map((t, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--green)', flexShrink: 0 }}>✓</span>
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
